<template>
  <div class="ai-sw">
    <!-- The user's own daily check: schedule (saved at once), state of the last run, a manual run for a day. -->
    <section class="ai-sw__run">
      <div class="ai-sw__schedule">
        <label class="ai-check">
          <input
            v-model="scheduleEnabled"
            type="checkbox"
            :disabled="!status"
            @change="saveSchedule"
          >
          <span>Ежедневная проверка</span>
        </label>
        <label class="ai-sw__inline">
          <span>в</span>
          <input
            v-model="scheduleTime"
            type="time"
            class="ai-input ai-sw__time"
            aria-label="Время ежедневной проверки"
            :disabled="!status || !scheduleEnabled"
            @change="saveSchedule"
          >
        </label>
        <span
          v-if="scheduleSaving"
          class="ai-spinner ai-spinner--small"
        />
        <span
          v-else-if="scheduleSaved"
          class="ai-sw__saved"
          role="status"
        >
          <ai-icon
            name="check"
            :size="13"
          />
          Сохранено
        </span>
      </div>
      <p
        v-if="scheduleError"
        class="ai-sw__line is-error"
        role="alert"
      >
        {{ scheduleError }}
      </p>
      <p
        v-else-if="pausedText"
        class="ai-sw__line ai-tone is-warning ai-sw__notice"
      >
        <ai-icon
          name="alert"
          :size="15"
        />
        <span>{{ pausedText }}</span>
      </p>

      <div class="ai-sw__state">
        <p
          v-if="running"
          class="ai-sw__line"
          role="status"
        >
          <span class="ai-spinner ai-spinner--small" />
          <span>{{ runningText }}</span>
        </p>
        <p
          v-else-if="queued"
          class="ai-sw__line"
          role="status"
        >
          <ai-icon
            name="clock"
            :size="15"
          />
          <span>{{ queuedText }}</span>
        </p>
        <template v-else-if="status && status.lastDay">
          <p class="ai-sw__line">
            <ai-icon
              :name="status.error ? 'alert' : 'check'"
              :size="15"
              :class="status.error ? 'is-danger' : 'is-success'"
            />
            <span>{{ lastText }}</span>
          </p>
          <p
            v-if="status.error"
            class="ai-sw__line ai-tone is-danger ai-sw__error"
          >
            Проверка завершилась с ошибкой: {{ status.error }}
          </p>
        </template>
        <p
          v-else-if="status"
          class="ai-sw__line is-muted"
        >
          Проверка ещё не выполнялась.
        </p>
        <p
          v-if="status && status.enabled && !status.paused"
          class="ai-sw__line is-muted"
        >
          <ai-icon
            name="clock"
            :size="15"
          />
          <span>{{ schedule }}</span>
        </p>
        <details
          v-if="!running && !queued && gaps.length"
          class="ai-sw__gaps"
        >
          <summary>Ограничения проверки ({{ gaps.length }})</summary>
          <ul>
            <li
              v-for="(gap, index) in gaps"
              :key="index"
            >
              {{ gap }}
            </li>
          </ul>
        </details>
      </div>
      <div class="ai-sw__run-form">
        <label
          class="ai-sw__label"
          for="ai-sw-run-day"
        >
          Проверить день
        </label>
        <input
          id="ai-sw-run-day"
          v-model="runDay"
          type="date"
          class="ai-input ai-sw__date"
          :min="minDay"
          :max="today"
        >
        <button
          type="button"
          class="ai-button is-primary"
          :disabled="!!running || !!queued || starting || !runDay"
          @click="run"
        >
          <span
            v-if="starting"
            class="ai-spinner ai-spinner--small"
          />
          <ai-icon
            v-else
            name="search"
            :size="14"
          />
          Проверить сейчас
        </button>
      </div>
      <p
        v-if="runMessage"
        class="ai-sw__line"
        :class="runFailed ? 'is-error' : 'is-muted'"
        role="status"
      >
        {{ runMessage }}
      </p>
    </section>

    <div class="ai-sw__toolbar">
      <div
        class="ai-segmented"
        role="tablist"
        aria-label="Статус находок"
      >
        <button
          v-for="item in filters"
          :key="item.key"
          type="button"
          role="tab"
          :aria-selected="filter === item.key ? 'true' : 'false'"
          :class="{ 'is-active': filter === item.key }"
          @click="setFilter(item.key)"
        >
          {{ item.label }}
        </button>
      </div>
      <div class="ai-sw__day">
        <label
          class="ai-sw__label"
          for="ai-sw-day"
        >
          День
        </label>
        <input
          id="ai-sw-day"
          v-model="day"
          type="date"
          class="ai-input ai-sw__date"
          :max="today"
          @change="load"
        >
        <button
          v-if="day"
          type="button"
          class="ai-link-button"
          @click="clearDay"
        >
          все дни
        </button>
      </div>
      <button
        type="button"
        class="ai-icon-button ai-sw__refresh"
        title="Обновить список"
        :disabled="loading"
        @click="load"
      >
        <ai-icon name="resume" />
      </button>
    </div>

    <p
      v-if="error"
      class="ai-sw__line is-error"
    >
      {{ error }}
    </p>
    <p
      v-else-if="loading && !items.length"
      class="ai-sw__line is-muted"
    >
      <span class="ai-spinner ai-spinner--small" />
      Загружаю находки…
    </p>
    <p
      v-else-if="!items.length"
      class="ai-sw__empty"
    >
      {{ emptyText }}
    </p>

    <template v-for="group in groups">
      <h4
        :key="`day-${group.day}`"
        class="ai-sw__day-title"
      >
        {{ dayLabel(group.day) }}
        <span>{{ countLabel(group.items.length) }}</span>
      </h4>
      <finding-card
        v-for="finding in group.items"
        :key="finding.id"
        :finding="finding"
        :busy="!!busy[finding.id]"
        :show-status="filter === 'all'"
        @status="review(finding, $event)"
      />
    </template>
    <p
      v-if="reviewError"
      class="ai-sw__line is-error"
      role="alert"
    >
      {{ reviewError }}
    </p>
  </div>
</template>

<script>
  import dayjs from 'dayjs';
  import * as api from '@/api/methods/assistant';
  import AiIcon from './AiIcon.vue';
  import FindingCard from './FindingCard.vue';
  import { count } from './format';

  const FILTERS = [
    { key: 'new', label: 'Новые' },
    { key: 'seen', label: 'Просмотренные' },
    { key: 'dismissed', label: 'Отклонённые' },
    { key: 'all', label: 'Все' },
  ];
  const EMPTY = {
    new: 'Новых находок нет.',
    seen: 'Просмотренных находок нет.',
    dismissed: 'Отклонённых находок нет.',
    all: 'Находок нет.',
  };
  const POLL_MS = 5000;
  const SAVED_MS = 2500;
  const DAY = 'YYYY-MM-DD';
  const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
  // The service checks days of the last 92 days.
  const MAX_AGE_DAYS = 92;
  // Why the daily check of this account does not run (the service's `paused`).
  const PAUSED = {
    inactive: 'Проверка приостановлена: учётная запись отключена или давно не использовалась.',
    'no-rights': 'Проверка приостановлена: у учётной записи нет прав администратора.',
  };

  export default {
    name: 'SideworkFindings',
    components: { AiIcon, FindingCard },
    data() {
      const today = dayjs();
      return {
        filters: FILTERS,
        filter: 'new',
        day: '',
        items: [],
        // GET /sidework/status: the user's last run, the running or queued one and the schedule.
        status: null,
        scheduleEnabled: true,
        scheduleTime: '07:00',
        scheduleSaving: false,
        scheduleAgain: false,
        scheduleSaved: false,
        scheduleError: '',
        savedTimer: null,
        loading: false,
        error: '',
        busy: {},
        reviewError: '',
        today: today.format(DAY),
        minDay: today.subtract(MAX_AGE_DAYS, 'day').format(DAY),
        runDay: today.subtract(1, 'day').format(DAY),
        starting: false,
        runMessage: '',
        runFailed: false,
        timer: null,
      };
    },
    computed: {
      running() {
        return (this.status && this.status.running) || null;
      },
      queued() {
        return (this.status && this.status.queued) || null;
      },
      active() {
        return !!(this.running || this.queued);
      },
      gaps() {
        return (this.status && this.status.gaps) || [];
      },
      runningText() {
        const { day, startedAt } = this.running;
        const started = startedAt ? `, начата ${this.stamp(startedAt)}` : '';
        return `Идёт проверка за ${this.dayLabel(day)}${started}. Список обновится, когда она закончится.`;
      },
      queuedText() {
        return `Проверка за ${this.dayLabel(this.queued.day)} в очереди: начнётся, когда закончится текущая.`;
      },
      pausedText() {
        const { status } = this;
        return (status && status.enabled && PAUSED[status.paused]) || '';
      },
      lastText() {
        const { lastDay, finishedAt, error } = this.status;
        const finished = finishedAt ? `, завершена ${this.stamp(finishedAt)}` : '';
        return `Последняя проверка — за ${this.dayLabel(lastDay)}${finished}.${error ? '' : ` ${this.summary}`}`;
      },
      summary() {
        const { status } = this;
        const levels = status.levels || {};
        const parts = [`Найдено: ${status.found || 0}`];
        const split = [['high', 'высокий'], ['medium', 'средний'], ['low', 'низкий']]
          .filter(([key]) => levels[key])
          .map(([key, label]) => `${label} — ${levels[key]}`);
        if (split.length) parts[0] += ` (${split.join(', ')})`;
        if (status.below) parts.push(`ниже порога — ${status.below}`);
        if (status.computers != null) {
          parts.push(`проверено ${count(status.computers, ['компьютер', 'компьютера', 'компьютеров'])}`);
        }
        return `${parts.join(', ')}.`;
      },
      schedule() {
        return `Каждый день в ${this.status.time || '—'} по времени системы мониторинга — за предыдущий день, `
          + 'по доступным вам компьютерам.';
      },
      emptyText() {
        const text = EMPTY[this.filter];
        return this.day ? `${text.slice(0, -1)} за ${this.dayLabel(this.day)}.` : text;
      },
      groups() {
        const result = [];
        this.items.forEach((item) => {
          const last = result[result.length - 1];
          if (last && last.day === item.day) last.items.push(item);
          else result.push({ day: item.day, items: [item] });
        });
        return result;
      },
    },
    watch: {
      active(value) {
        if (value) this.startPolling();
      },
    },
    created() {
      this.load();
    },
    activated() {
      // Back on the tab: the list may have changed (the badge, another administrator).
      if (this.status) this.load();
    },
    beforeDestroy() {
      this.stopPolling();
      clearTimeout(this.savedTimer);
    },
    methods: {
      dayLabel(value) {
        const moment = dayjs(value);
        return moment.isValid() ? moment.format('DD.MM.YYYY') : String(value || '');
      },
      // Service timestamps are UTC (ISO): shown in the browser time zone.
      stamp(value) {
        const moment = dayjs(value);
        if (!moment.isValid()) return '';
        if (moment.isSame(dayjs(), 'day')) return `сегодня в ${moment.format('HH:mm')}`;
        return moment.format('DD.MM.YYYY HH:mm');
      },
      countLabel(value) {
        return count(value, ['находка', 'находки', 'находок']);
      },
      setFilter(key) {
        if (this.filter === key) return;
        this.filter = key;
        this.load();
      },
      clearDay() {
        this.day = '';
        this.load();
      },
      async load() {
        this.loading = true;
        this.error = '';
        this.reviewError = '';
        const params = {};
        if (this.filter !== 'all') params.status = this.filter;
        if (this.day) params.day = this.day;
        try {
          const { data } = await api.listFindings(params);
          this.items = data.items || [];
          this.status = data.status || null;
          if (this.status && !this.scheduleSaving) this.applySchedule(this.status);
        } catch (error) {
          this.error = api.errorText(error, 'Не удалось загрузить находки');
        } finally {
          this.loading = false;
        }
      },
      async run() {
        this.starting = true;
        this.runMessage = '';
        this.runFailed = false;
        try {
          const { data } = await api.runSidework(this.runDay);
          const entry = { day: data.day || this.runDay, startedAt: null };
          this.status = {
            ...(this.status || {}),
            running: data.queued ? null : entry,
            queued: data.queued ? entry : null,
          };
        } catch (error) {
          const conflict = error && error.response && error.response.status === 409;
          this.runFailed = !conflict;
          this.runMessage = api.errorText(error, 'Не удалось начать проверку');
          if (conflict) this.startPolling();
        } finally {
          this.starting = false;
        }
      },
      startPolling() {
        if (this.timer) return;
        this.timer = setInterval(this.poll, POLL_MS);
      },
      stopPolling() {
        clearInterval(this.timer);
        this.timer = null;
      },
      async poll() {
        try {
          const { data } = await api.getSideworkStatus();
          this.status = data;
          if (data.running || data.queued) return;
        } catch (error) {
          return; // the next tick tries again
        }
        this.stopPolling();
        this.runMessage = '';
        await this.load();
        this.$store.dispatch('assistant/refreshCapabilities');
      },
      applySchedule({ enabled, time }) {
        this.scheduleEnabled = enabled !== false;
        this.scheduleTime = time || '07:00';
      },
      // Saved at once; a change made while saving is saved right after.
      async saveSchedule() {
        if (this.scheduleSaving) {
          this.scheduleAgain = true;
          return;
        }
        this.scheduleError = '';
        this.scheduleSaved = false;
        if (!TIME.test(this.scheduleTime)) {
          this.scheduleError = 'Укажите время ежедневной проверки.';
          return;
        }
        this.scheduleSaving = true;
        try {
          const { data } = await api.saveSideworkSchedule(this.scheduleEnabled, this.scheduleTime);
          this.status = { ...(this.status || {}), ...data };
          if (!this.scheduleAgain) {
            this.applySchedule(data);
            this.scheduleSaved = true;
            clearTimeout(this.savedTimer);
            this.savedTimer = setTimeout(() => {
              this.scheduleSaved = false;
            }, SAVED_MS);
          }
        } catch (error) {
          this.scheduleError = api.errorText(error, 'Не удалось сохранить расписание проверки');
          if (this.status && !this.scheduleAgain) this.applySchedule(this.status);
        } finally {
          this.scheduleSaving = false;
          if (this.scheduleAgain) {
            this.scheduleAgain = false;
            this.saveSchedule();
          }
        }
      },
      async review(finding, status) {
        this.$set(this.busy, finding.id, true);
        this.reviewError = '';
        try {
          const { data } = await api.updateFinding(finding.id, status);
          const index = this.items.findIndex((item) => item.id === finding.id);
          if (index >= 0) {
            // Out of the current filter: the finding leaves the list.
            if (this.filter !== 'all' && data.status !== this.filter) this.items.splice(index, 1);
            else this.items.splice(index, 1, { ...finding, ...data });
          }
          this.$store.dispatch('assistant/refreshCapabilities');
        } catch (error) {
          this.reviewError = api.errorText(error, 'Не удалось изменить статус находки');
        } finally {
          this.$delete(this.busy, finding.id);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ai-sw {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .ai-sw__run {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px;
    border: 1px solid var(--ui-border);
    border-radius: var(--ui-radius);
  }

  .ai-sw__state {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .ai-sw__line {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 0;
    color: var(--ui-text);
    font-size: 13px;
    line-height: 1.45;

    .ai-icon {
      margin-top: 1px;
    }

    .ai-spinner {
      margin-top: 3px;
    }

    &.is-muted {
      color: var(--ui-text-muted);
    }

    &.is-error {
      color: var(--ui-danger);
    }

    .is-danger {
      color: var(--ui-danger);
    }

    .is-success {
      color: var(--ui-success);
    }
  }

  .ai-sw__error,
  .ai-sw__notice {
    padding: 6px 10px;
    border-radius: var(--ui-radius-sm);
  }

  .ai-sw__schedule {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 12px;
    min-height: 34px;

    .ai-check {
      font-weight: 500;
    }
  }

  .ai-sw__inline {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    color: var(--ui-text-muted);
    font-size: 13px;
  }

  .ai-widget .ai-sw__time {
    width: auto;
  }

  .ai-sw__saved {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--ui-text-muted);
    font-size: 12px;

    .ai-icon {
      color: var(--ui-success);
    }
  }

  .ai-sw__gaps {
    color: var(--ui-text-muted);
    font-size: 13px;

    summary {
      cursor: pointer;
    }

    ul {
      margin: 4px 0 0;
      padding-left: 20px;
      list-style: disc;
    }

    li {
      display: list-item;
    }
  }

  .ai-sw__run-form,
  .ai-sw__toolbar,
  .ai-sw__day {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  .ai-sw__toolbar {
    gap: 8px 12px;

    // Four filters do not fit a phone: they scroll sideways instead of overflowing the window.
    .ai-segmented {
      max-width: 100%;
      overflow-x: auto;
    }
  }

  .ai-sw__label {
    margin: 0;
    color: var(--ui-text-muted);
    font-size: 13px;
  }

  .ai-widget .ai-sw__date {
    width: auto;
    min-width: 140px;
  }

  .ai-sw__refresh {
    margin-left: auto;
  }

  .ai-sw__empty {
    margin: 8px 0;
    color: var(--ui-text-muted);
    font-size: 13px;
    text-align: center;
  }

  @media (max-width: 900px) {
    .ai-sw__toolbar .ai-segmented button {
      padding: 4px 8px;
    }
  }

  .ai-sw__day-title {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin: 6px 0 -2px;
    color: var(--ui-text);
    font-size: 13px;
    font-weight: 500;

    span {
      color: var(--ui-text-muted);
      font-weight: 400;
    }
  }
</style>
